import type {
  AgentGender,
  AgentBodyType,
  AgentEthnicity,
  AgentHairType,
  AgentBeard,
} from "../interfaces";

/**
 * Attribute Rules and Dependencies
 * Defines which attribute options are compatible with each other
 */

// Body types that work well with each gender
export const BODY_TYPE_BY_GENDER: Record<AgentGender, AgentBodyType[]> = {
  male: ['slim', 'average', 'athletic', 'muscular', 'stocky', 'petite'],
  female: ['slim', 'average', 'athletic', 'soft-average', 'curvy', 'full', 'petite'],
  'non-binary': ['slim', 'average', 'athletic', 'soft-average', 'curvy', 'muscular', 'full', 'stocky', 'petite'], // All options
};

// Beard styles available by gender
export const BEARD_BY_GENDER: Record<AgentGender, AgentBeard[]> = {
  male: ['none', 'stubble', 'short', 'medium', 'full', 'goatee', 'van dyke', 'mustache', 'soul patch'],
  'non-binary': ['none', 'stubble', 'short', 'medium', 'full', 'goatee', 'van dyke', 'mustache', 'soul patch'],
  female: ['none'], // Typically no facial hair, but can be set if needed
};

// Hair types that are common/natural for each ethnicity
export const HAIR_TYPE_BY_ETHNICITY: Record<AgentEthnicity, AgentHairType[]> = {
  African: ['coily', 'kinky', 'afro', 'locs', 'braided', 'twists', 'curly', 'bald', 'shaved'],
  'East Asian': ['straight', 'wavy', 'bald', 'shaved'],
  'South Asian': ['straight', 'wavy', 'curly', 'bald', 'shaved'],
  'Middle Eastern': ['straight', 'wavy', 'curly', 'bald', 'shaved'],
  European: ['straight', 'wavy', 'curly', 'bald', 'shaved'],
  Latino: ['straight', 'wavy', 'curly', 'bald', 'shaved'],
  American: ['straight', 'wavy', 'curly', 'coily', 'kinky', 'afro', 'locs', 'braided', 'twists', 'bald', 'shaved'], // Diverse
  Caribbean: ['straight', 'wavy', 'curly', 'coily', 'kinky', 'afro', 'locs', 'braided', 'twists', 'bald', 'shaved'], // Diverse
  Mixed: ['straight', 'wavy', 'curly', 'coily', 'kinky', 'afro', 'locs', 'braided', 'twists', 'bald', 'shaved'], // All options
};

/**
 * Get filtered body type options based on gender
 */
export const getBodyTypeOptions = (gender?: AgentGender) => {
  const allOptions = [
    { value: 'slim', label: 'Slim' },
    { value: 'average', label: 'Average' },
    { value: 'athletic', label: 'Athletic' },
    { value: 'soft-average', label: 'Soft Average' },
    { value: 'curvy', label: 'Curvy' },
    { value: 'muscular', label: 'Muscular' },
    { value: 'full', label: 'Full' },
    { value: 'stocky', label: 'Stocky' },
    { value: 'petite', label: 'Petite' },
  ];

  if (!gender) return allOptions;

  const allowedTypes = BODY_TYPE_BY_GENDER[gender];
  return allOptions.filter((option) =>
    allowedTypes.includes(option.value as AgentBodyType)
  );
};

/**
 * Get filtered beard options based on gender
 */
export const getBeardOptions = (gender?: AgentGender) => {
  const allOptions = [
    { value: 'none', label: 'None (Clean Shaven)' },
    { value: 'stubble', label: 'Stubble' },
    { value: 'short', label: 'Short' },
    { value: 'medium', label: 'Medium' },
    { value: 'full', label: 'Full' },
    { value: 'goatee', label: 'Goatee' },
    { value: 'van dyke', label: 'Van Dyke' },
    { value: 'mustache', label: 'Mustache' },
    { value: 'soul patch', label: 'Soul Patch' },
  ];

  if (!gender) return allOptions;

  const allowedBeards = BEARD_BY_GENDER[gender];
  return allOptions.filter((option) =>
    allowedBeards.includes(option.value as AgentBeard)
  );
};

/**
 * Get filtered hair type options based on ethnicity
 */
export const getHairTypeOptions = (ethnicity?: AgentEthnicity) => {
  const allOptions = [
    { value: 'straight', label: 'Straight' },
    { value: 'wavy', label: 'Wavy' },
    { value: 'curly', label: 'Curly' },
    { value: 'coily', label: 'Coily' },
    { value: 'kinky', label: 'Kinky' },
    { value: 'afro', label: 'Afro' },
    { value: 'locs', label: 'Locs' },
    { value: 'braided', label: 'Braided' },
    { value: 'twists', label: 'Twists' },
    { value: 'bald', label: 'Bald' },
    { value: 'shaved', label: 'Shaved' },
  ];

  if (!ethnicity) return allOptions;

  const allowedHairTypes = HAIR_TYPE_BY_ETHNICITY[ethnicity];
  return allOptions.filter((option) =>
    allowedHairTypes.includes(option.value as AgentHairType)
  );
};

/**
 * Validate if a body type is compatible with a gender
 */
export const isBodyTypeValidForGender = (
  bodyType?: AgentBodyType,
  gender?: AgentGender
): boolean => {
  if (!bodyType || !gender) return true;
  return BODY_TYPE_BY_GENDER[gender].includes(bodyType);
};

/**
 * Validate if a beard style is compatible with a gender
 */
export const isBeardValidForGender = (
  beard?: AgentBeard,
  gender?: AgentGender
): boolean => {
  if (!beard || !gender) return true;
  return BEARD_BY_GENDER[gender].includes(beard);
};

/**
 * Validate if a hair type is compatible with an ethnicity
 */
export const isHairTypeValidForEthnicity = (
  hairType?: AgentHairType,
  ethnicity?: AgentEthnicity
): boolean => {
  if (!hairType || !ethnicity) return true;
  return HAIR_TYPE_BY_ETHNICITY[ethnicity].includes(hairType);
};

/**
 * Get suggested default beard based on gender
 */
export const getDefaultBeard = (gender?: AgentGender): AgentBeard => {
  if (gender === 'female') return 'none';
  return 'none'; // Default to none for all
};

/**
 * Auto-correct attributes when dependencies change
 */
export const autoCorrectAttributes = (
  attributes: {
    gender?: AgentGender;
    bodyType?: AgentBodyType;
    ethnicity?: AgentEthnicity;
    hairType?: AgentHairType;
    beard?: AgentBeard;
  }
): {
  bodyType?: AgentBodyType;
  hairType?: AgentHairType;
  beard?: AgentBeard;
} => {
  const corrections: {
    bodyType?: AgentBodyType;
    hairType?: AgentHairType;
    beard?: AgentBeard;
  } = {};

  // Auto-correct body type if incompatible with gender
  if (attributes.bodyType && attributes.gender) {
    if (!isBodyTypeValidForGender(attributes.bodyType, attributes.gender)) {
      corrections.bodyType = undefined;
    }
  }

  // Auto-correct beard if incompatible with gender
  if (attributes.beard && attributes.gender) {
    if (!isBeardValidForGender(attributes.beard, attributes.gender)) {
      corrections.beard = getDefaultBeard(attributes.gender);
    }
  }

  // Auto-correct hair type if incompatible with ethnicity
  if (attributes.hairType && attributes.ethnicity) {
    if (!isHairTypeValidForEthnicity(attributes.hairType, attributes.ethnicity)) {
      corrections.hairType = undefined;
    }
  }

  return corrections;
};
